/**
 * Edit interest view — UI only; state from useEditInterestViewModel.
 */

import { ArrowLeft } from 'lucide-react'
import { LoadingSpinner } from '@/features/common/components/LoadingSpinner'
import { useEditInterestViewModel } from '../viewmodels/use_edit_interest_viewmodel'

export function EditInterestPageView() {
  const { fileInputRef, interest, isLoading, form, actions } = useEditInterestViewModel()
  const { interestName, selectedFile, isActive, fileName, imagePreview, errors, isSubmitting } =
    form

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={actions.cancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Edit Interest</h1>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  if (!interest) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={actions.cancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Edit Interest</h1>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <p className="text-red-600 font-medium">Interest not found</p>
          <p className="text-sm text-gray-500 mt-2">
            The interest you're trying to edit does not exist.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={actions.cancel}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Edit Interest</h1>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form
          onSubmit={e => {
            e.preventDefault()
            void actions.submit()
          }}
          className="space-y-6"
        >
          {/* Interest Name */}
          <div>
            <label htmlFor="interestName" className="block text-sm font-medium text-gray-700 mb-2">
              Interest Name<span className="text-red-500">*</span>
            </label>
            <input
              id="interestName"
              type="text"
              value={interestName}
              onChange={e => {
                actions.setInterestName(e.target.value)
                actions.clearFieldError('name')
              }}
              placeholder="Enter Interest Name"
              className={`w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Icon */}
          <div>
            <label htmlFor="icon" className="block text-sm font-medium text-gray-700 mb-2">
              Icon
            </label>
            <div className="space-y-4">
              {/* Image Preview */}
              {imagePreview && (
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 border border-gray-300 rounded-md overflow-hidden bg-gray-50 flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="Interest icon preview"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        // Hide preview if image fails to load
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1">Current Preview</p>
                    <p className="text-xs text-gray-500">{fileName}</p>
                  </div>
                </div>
              )}
              
              {/* File Input */}
              <div className="flex items-center gap-4">
                <input
                  ref={fileInputRef}
                  id="icon"
                  type="file"
                  accept="image/*"
                  onChange={e => actions.handleFileChange(e.target.files?.[0])}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={actions.openFilePicker}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {imagePreview ? 'Change File' : 'Choose File'}
                </button>
                {selectedFile && (
                  <button
                    type="button"
                    onClick={actions.removeSelectedFile}
                    className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                  >
                    Remove
                  </button>
                )}
                {!imagePreview && (
                  <span className="text-sm text-gray-500">{fileName}</span>
                )}
              </div>
            </div>
            {errors.icon && <p className="mt-1 text-sm text-red-600">{errors.icon}</p>}
          </div>

          {/* Active Checkbox */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => actions.setIsActive(e.target.checked)}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={actions.cancel}
              className="px-6 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#019b9a' }}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner size="sm" />
                  Updating...
                </span>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

